import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import Layout from './layout/Layout';
import Card from './ui/Card';
import Button from './ui/Button';
import Spinner from './ui/Spinner';
import Table from './ui/Table';
import { drillholeValidationService } from '../services/drillholeValidationService';
import { drillingService } from '../services/drillingService';
import {
  pickLatestProjectFromSources,
  resolveSourceProjectName,
} from '../utils/drillingCampaignHelpers';
import { exportToCSV } from '../utils/csvExport';
import './DrillholeValidation.css';

// rule: 'depth_match' = max_to_depth must >= collar_actual_depth
// rule: 'survey_30m'  = collar_actual_depth - survey_max_depth <= 30
// rule: 'has_records'  = row_count > 0
// rule: 'core_orientation_or_steep' = max_to_depth >= EOH OR survey_min_steepness < -80
const LOGGING_CATEGORIES = [
  { key: 'survey',         depthKey: 'survey_max_depth',              countKey: 'survey_row_count',         label: 'Survey',         abbr: 'SRV',  rule: 'survey_30m' },
  { key: 'lithology',      depthKey: 'lithology_max_to_depth',        countKey: 'lithology_row_count',      label: 'Lithology',      abbr: 'LITH', rule: 'depth_match' },
  { key: 'alteration',     depthKey: 'alteration_max_to_depth',       countKey: 'alteration_row_count',     label: 'Alteration',     abbr: 'ALT',  rule: 'has_records' },
  { key: 'mineralization', depthKey: 'mineralization_max_to_depth',   countKey: 'mineralization_row_count',  label: 'Mineralization', abbr: 'MIN',  rule: 'has_records' },
  { key: 'veining',        depthKey: 'veining_max_to_depth',          countKey: 'veining_row_count',        label: 'Veining',        abbr: 'VN',   rule: 'has_records' },
  { key: 'structure',      depthKey: 'structure_max_to_depth',        countKey: 'structure_row_count',      label: 'Structure',      abbr: 'STR',  rule: 'has_records' },
  { key: 'oxidation',      depthKey: 'oxidation_max_to_depth',        countKey: 'oxidation_row_count',      label: 'Oxidation',      abbr: 'OX',   rule: 'depth_match' },
  { key: 'mag_susc',       depthKey: 'mag_susc_max_to_depth',         countKey: 'mag_susc_row_count',       label: 'Mag Susc',       abbr: 'MAG',  rule: 'depth_match' },
  { key: 'rqd',            depthKey: 'rqd_max_to_depth',              countKey: 'rqd_row_count',            label: 'RQD',            abbr: 'RQD',  rule: 'depth_match' },
  { key: 'corestorage',    depthKey: 'corestorage_max_to_depth',      countKey: 'corestorage_row_count',    label: 'Core Storage',   abbr: 'CS',   rule: 'depth_match' },
  { key: 'core_orientation', depthKey: 'core_orientation_max_to_depth', countKey: 'core_orientation_row_count', label: 'Core Orientation', abbr: 'COR', rule: 'core_orientation_or_steep' },
  { key: 'samples',        depthKey: 'samples_max_to_depth',          countKey: 'samples_row_count',        label: 'Samples',        abbr: 'SMP',  rule: 'has_records' },
];

const TOTAL_CHECKS = LOGGING_CATEGORIES.length + 1; // +1 for actual coordinates

const CHECK_FILTER_OPTIONS = [
  { value: '', label: 'All Checks (no filter)' },
  ...LOGGING_CATEGORIES.map(cat => {
    let suffix = '';
    if (cat.rule === 'depth_match') suffix = ' (reaches depth)';
    else if (cat.rule === 'survey_30m') suffix = ' (within 30m)';
    else if (cat.rule === 'has_records') suffix = ' (has records)';
    else if (cat.rule === 'core_orientation_or_steep') suffix = ' (reaches EOH or steep exempt)';
    return { value: cat.key, label: `${cat.label}${suffix}` };
  }),
  { value: 'actual_coords', label: 'Has Actual/Final Coordinates' },
];

function toNum(val) {
  if (val == null || val === '') return null;
  const n = Number(val);
  return isNaN(n) ? null : n;
}

function parsePostgresArray(val) {
  if (!val) return [];
  if (Array.isArray(val)) return val;
  const str = String(val).replace(/^\{/, '').replace(/\}$/, '');
  if (!str) return [];
  return str.split(',').map(s => s.trim().replace(/^"|"$/g, ''));
}

/** Human-readable coordinate type labels (preserves casing from source). */
function getCoordinateTypeLabels(hole) {
  let raw = hole.coordinate_types ?? hole.coordinate_type;
  if (raw == null || raw === '') return [];
  if (Array.isArray(raw)) {
    return raw.map(t => String(t).trim()).filter(Boolean);
  }
  let str = String(raw).trim();
  if ((str.startsWith('[') && str.endsWith(']')) || (str.startsWith('"[') && str.endsWith(']"'))) {
    try {
      const parsed = JSON.parse(str.replace(/^"|"$/g, ''));
      if (Array.isArray(parsed)) {
        return parsed.map(t => String(t).trim()).filter(Boolean);
      }
    } catch {
      /* fall through */
    }
  }
  if (str.startsWith('{') && str.endsWith('}')) {
    return parsePostgresArray(str);
  }
  return str.split(',').map(s => s.trim()).filter(Boolean);
}

function humanize(snakeStr) {
  return snakeStr.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

/** Passes if any coordinate type is Actual or Final (Final is highest confidence). */
function hasActualCoordinates(hole) {
  const labels = getCoordinateTypeLabels(hole);
  return labels.some(t => {
    const lower = t.toLowerCase();
    return lower === 'actual' || lower === 'final';
  });
}

/** Very steep holes (min survey steepness below -80) exempt from core orientation logging. */
function isSteepnessExempt(hole) {
  const minSteep = toNum(hole.survey_min_steepness);
  return minSteep != null && minSteep < -80;
}

function isCategoryComplete(hole, cat) {
  const depth = toNum(hole.collar_actual_depth);
  const maxDepth = toNum(hole[cat.depthKey]);
  const rowCount = toNum(hole[cat.countKey]);

  switch (cat.rule) {
    case 'depth_match':
      return depth != null && maxDepth != null && maxDepth >= depth;
    case 'survey_30m':
      return depth != null && maxDepth != null && (depth - maxDepth) <= 30;
    case 'has_records':
      return rowCount != null && rowCount > 0;
    case 'core_orientation_or_steep':
      if (isSteepnessExempt(hole)) return true;
      return depth != null && rowCount != null && rowCount > 0
        && maxDepth != null && maxDepth >= depth;
    default:
      return false;
  }
}

function getCategoryBarColor(hole, cat) {
  if (isCategoryComplete(hole, cat)) return '#10b981';
  const maxDepth = toNum(hole[cat.depthKey]);
  const rowCount = toNum(hole[cat.countKey]);
  if (cat.rule === 'has_records') {
    return (rowCount != null && rowCount > 0) ? '#10b981' : '#ef4444';
  }
  if (cat.rule === 'core_orientation_or_steep' && (!maxDepth || maxDepth <= 0) && !isSteepnessExempt(hole)) {
    return '#ef4444';
  }
  if (!maxDepth || maxDepth <= 0) return '#ef4444';
  const depth = toNum(hole.collar_actual_depth);
  if (!depth) return '#ef4444';
  if (cat.rule === 'survey_30m') {
    const gap = depth - maxDepth;
    if (gap <= 30) return '#10b981';
    if (gap <= 45) return '#f59e0b';
    return '#f97316';
  }
  const pct = maxDepth / depth;
  if (pct >= 1) return '#10b981';
  if (pct >= 0.75) return '#f59e0b';
  return '#f97316';
}

function getCategoryDotClass(hole, cat) {
  if (isCategoryComplete(hole, cat)) return 'status-complete';
  const maxDepth = toNum(hole[cat.depthKey]);
  const rowCount = toNum(hole[cat.countKey]);
  if (cat.rule === 'has_records') {
    return (rowCount != null && rowCount > 0) ? 'status-complete' : 'status-missing';
  }
  if (cat.rule === 'core_orientation_or_steep' && isSteepnessExempt(hole)) {
    return 'status-complete';
  }
  if (cat.rule === 'core_orientation_or_steep' && (!maxDepth || maxDepth <= 0)) {
    return 'status-missing';
  }
  if (!maxDepth || maxDepth <= 0) return 'status-missing';
  const depth = toNum(hole.collar_actual_depth);
  if (!depth) return 'status-missing';
  if (cat.rule === 'survey_30m') {
    const gap = depth - maxDepth;
    if (gap <= 30) return 'status-complete';
    if (gap <= 45) return 'status-partial';
    return 'status-incomplete';
  }
  const pct = maxDepth / depth;
  if (pct >= 1) return 'status-complete';
  if (pct >= 0.75) return 'status-partial';
  return 'status-incomplete';
}

function holeFailsCheck(hole, checkKey) {
  if (!checkKey) return false;
  if (checkKey === 'actual_coords') return !hasActualCoordinates(hole);
  const cat = LOGGING_CATEGORIES.find(c => c.key === checkKey);
  if (!cat) return false;
  return !isCategoryComplete(hole, cat);
}

function computeCompletion(hole) {
  const depth = toNum(hole.collar_actual_depth);
  if (!depth) return 0;
  let passed = 0;
  for (const cat of LOGGING_CATEGORIES) {
    if (isCategoryComplete(hole, cat)) passed++;
  }
  if (hasActualCoordinates(hole)) passed++;
  return Math.round((passed / TOTAL_CHECKS) * 100);
}

function getStatusBadgeClass(status) {
  if (!status) return 'badge-unknown';
  const s = status.toLowerCase();
  if (s === 'complete' || s === 'completed') return 'badge-complete';
  if (s === 'inprogress' || s === 'in progress' || s === 'in_progress') return 'badge-inprogress';
  if (s === 'planned') return 'badge-planned';
  if (s === 'abandoned') return 'badge-abandoned';
  return 'badge-unknown';
}

const DrillholeValidation = ({ user }) => {
  const [validationData, setValidationData] = useState([]);
  /** Active campaigns from drilling_scmi.project_sources. */
  const [projectSources, setProjectSources] = useState([]);
  /** Per-project sample metrics (for source_project_id ? source_project_name labels). */
  const [sampleProgressBundle, setSampleProgressBundle] = useState(null);
  /** 'all' | source_project_name � filters table and summary stats. */
  const [selectedProjectName, setSelectedProjectName] = useState('all');
  /** After project_sources loads, select default project tab once (highest priority). */
  const initialCampaignTabAppliedRef = useRef(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [checkFilter, setCheckFilter] = useState('');
  const [selectedHoleId, setSelectedHoleId] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (initialCampaignTabAppliedRef.current) return;
    if (!projectSources.length) return;
    initialCampaignTabAppliedRef.current = true;
    const latest = pickLatestProjectFromSources(projectSources);
    const name = resolveSourceProjectName(
      latest?.project_id,
      sampleProgressBundle?.projects
    );
    if (name != null && String(name) !== '') {
      setSelectedProjectName(String(name));
    }
  }, [projectSources, sampleProgressBundle]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [validationResult, sampleBundle, sourcesList] = await Promise.all([
        drillholeValidationService.getDashboardData(),
        drillingService.fetchSampleProgressDashboard().catch((e) => {
          console.warn('sample_progress unavailable:', e);
          return null;
        }),
        drillingService.fetchProjectSources().catch((e) => {
          console.warn('project_sources unavailable:', e);
          return [];
        }),
      ]);
      setValidationData(validationResult.validationData || []);
      setSampleProgressBundle(sampleBundle);
      setProjectSources(Array.isArray(sourcesList) ? sourcesList : []);
    } catch (err) {
      console.error('Error loading drillhole validation data:', err);
      setError(err.message || 'Failed to load drillhole validation data');
    } finally {
      setLoading(false);
    }
  };

  const campaignTabList = useMemo(() => {
    if (projectSources.length > 0) return projectSources;
    const names = [
      ...new Set(
        validationData.map((r) => r.source_project_name).filter(Boolean)
      ),
    ].sort();
    return names.map((source_project_name) => ({ source_project_name }));
  }, [projectSources, validationData]);

  const campaignTabLabel = useCallback(
    (src) => {
      if (src.source_project_name) return String(src.source_project_name);
      const name = resolveSourceProjectName(
        src.project_id,
        sampleProgressBundle?.projects
      );
      return name || String(src.project_id || 'Project');
    },
    [sampleProgressBundle]
  );

  const campaignTabKey = useCallback(
    (src) => {
      if (src.source_project_name) return String(src.source_project_name);
      return campaignTabLabel(src);
    },
    [campaignTabLabel]
  );

  /** Holes for the selected campaign tab (filter key: source_project_name). */
  const campaignBaseData = useMemo(() => {
    if (selectedProjectName === 'all') return validationData;
    return validationData.filter(
      (r) =>
        r.source_project_name != null &&
        String(r.source_project_name) === String(selectedProjectName)
    );
  }, [validationData, selectedProjectName]);

  useEffect(() => {
    if (!selectedHoleId) return;
    const stillHere = campaignBaseData.some((h) => h.hole_id === selectedHoleId);
    if (!stillHere) {
      setSelectedHoleId(null);
    }
  }, [campaignBaseData, selectedHoleId]);

  useEffect(() => {
    if (selectedProjectName === 'all') return;
    const valid = campaignTabList.some(
      (t) => campaignTabKey(t) === String(selectedProjectName)
    );
    if (campaignTabList.length > 0 && !valid) {
      setSelectedProjectName('all');
    }
  }, [campaignTabList, selectedProjectName, campaignTabKey]);

  const filteredData = useMemo(() => {
    let data = campaignBaseData;
    if (checkFilter) {
      data = data.filter(h => holeFailsCheck(h, checkFilter));
    }
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      data = data.filter(h =>
        (h.hole_id && h.hole_id.toLowerCase().includes(term)) ||
        (h.geologist && h.geologist.toLowerCase().includes(term)) ||
        (h.collar_status && h.collar_status.toLowerCase().includes(term))
      );
    }
    return data;
  }, [campaignBaseData, searchTerm, checkFilter]);

  const selectedHole = useMemo(() => {
    if (!selectedHoleId) return null;
    return campaignBaseData.find((h) => h.hole_id === selectedHoleId) || null;
  }, [campaignBaseData, selectedHoleId]);

  const summaryStats = useMemo(() => {
    const total = campaignBaseData.length;
    let complete = 0;
    let incomplete = 0;
    let missing = 0;
    let noActualCoords = 0;
    for (const hole of campaignBaseData) {
      const pct = computeCompletion(hole);
      if (pct === 100) complete++;
      else if (pct > 0) incomplete++;
      else missing++;
      if (!hasActualCoordinates(hole)) noActualCoords++;
    }
    return { total, complete, incomplete, missing, noActualCoords };
  }, [campaignBaseData]);

  const handleRowClick = useCallback((row) => {
    setSelectedHoleId(row.hole_id);
  }, []);

  const handleCloseDetail = useCallback(() => {
    setSelectedHoleId(null);
  }, []);

  const handleExportCSV = useCallback(() => {
    if (!filteredData.length) return;
    const rows = filteredData.map(hole => {
      const row = {
        hole_id: hole.hole_id,
        geologist: hole.geologist || '',
        geotech: hole.geotech || '',
        collar_actual_depth: toNum(hole.collar_actual_depth) ?? '',
        completion_pct: computeCompletion(hole),
      };
      for (const cat of LOGGING_CATEGORIES) {
        row[`${cat.key}_max_depth`] = toNum(hole[cat.depthKey]) ?? '';
        row[`${cat.key}_row_count`] = toNum(hole[cat.countKey]) ?? '';
      }
      row.coordinate_types = hole.coordinate_types || '';
      row.has_actual_coordinates = hasActualCoordinates(hole);
      const depth = toNum(hole.collar_actual_depth);
      const surveyMax = toNum(hole.survey_max_depth);
      row.survey_gap = (depth != null && surveyMax != null) ? depth - surveyMax : '';
      return row;
    });
    const datestamp = new Date().toISOString().split('T')[0];
    exportToCSV(`drillhole-validation-${datestamp}.csv`, rows);
  }, [filteredData]);

  const tableColumns = useMemo(() => [
    {
      key: 'hole_id',
      title: 'Hole ID',
      width: '130px',
      render: (val, row) => (
        <span className={`hole-id-cell ${selectedHoleId === row.hole_id ? 'selected' : ''}`}>
          {val}
        </span>
      )
    },
    {
      key: 'geologist',
      title: 'Geologist',
      width: '160px',
      render: (val) => <span className="geologist-cell">{val || '�'}</span>
    },
    {
      key: 'collar_actual_depth',
      title: 'Depth (m)',
      width: '90px',
      render: (val) => {
        const n = toNum(val);
        return <span className="depth-cell">{n != null ? n.toFixed(1) : '�'}</span>;
      }
    },
    {
      key: '_completion',
      title: 'Completion',
      width: '140px',
      sortable: false,
      render: (_val, row) => {
        const pct = computeCompletion(row);
        return (
          <div className="completion-bar-wrapper">
            <div className="completion-bar-track">
              <div
                className={`completion-bar-fill ${pct === 100 ? 'complete' : pct >= 50 ? 'partial' : 'low'}`}
                style={{ width: `${pct}%` }}
              />
            </div>
            <span className="completion-pct">{pct}%</span>
          </div>
        );
      }
    },
    {
      key: '_categories',
      title: 'Categories',
      width: '280px',
      sortable: false,
      render: (_val, row) => {
        const coordCls = hasActualCoordinates(row) ? 'status-complete' : 'status-missing';
        return (
          <div className="category-dots">
            {LOGGING_CATEGORIES.map(cat => {
              const cls = getCategoryDotClass(row, cat);
              const maxD = toNum(row[cat.depthKey]);
              const rc = toNum(row[cat.countKey]);
              let tip = `${cat.label}: `;
              if (cat.rule === 'has_records') {
                tip += rc ? `${rc} rows` : 'No data';
              } else if (cat.rule === 'core_orientation_or_steep' && isSteepnessExempt(row)) {
                tip += `Steep exempt (min steepness ${toNum(row.survey_min_steepness)})`;
              } else {
                tip += maxD != null ? `${maxD}m` : 'No data';
                if (cat.rule === 'core_orientation_or_steep' && rc) {
                  tip += ` (${rc} rows)`;
                }
              }
              return <span key={cat.key} className={`cat-dot ${cls}`} title={tip} />;
            })}
            <span className={`cat-dot ${coordCls}`} title={`Actual/Final Coords: ${hasActualCoordinates(row) ? 'Yes' : 'No'}`} />
          </div>
        );
      }
    }
  ], [selectedHoleId]);

  return (
    <Layout user={user} currentRoute="/drillhole-validation">
      <div className="dv">
        <div className="dv-header">
          <div className="dv-header-text">
            <h1>Drillhole Validation</h1>
            <p>Visual review of logging completeness for each drillhole</p>
          </div>
          <Button onClick={loadData} variant="secondary" size="sm">
            Refresh
          </Button>
        </div>

        {loading && (
          <div className="loading-container">
            <Spinner size="large" />
            <p>Loading drillhole validation data...</p>
          </div>
        )}

        {error && !loading && (
          <Card className="dv-error-card">
            <h3>Error Loading Data</h3>
            <p>{error}</p>
            <Button onClick={loadData}>Retry</Button>
          </Card>
        )}

        {!loading && !error && (
          <>
            {campaignTabList.length > 0 && (
              <div className="campaign-tab-bar" role="tablist" aria-label="Drilling campaign">
                <button
                  type="button"
                  role="tab"
                  aria-selected={selectedProjectName === 'all'}
                  className={`campaign-tab-btn${selectedProjectName === 'all' ? ' campaign-tab-btn--active' : ''}`}
                  onClick={() => setSelectedProjectName('all')}
                >
                  All
                </button>
                {campaignTabList.map((src) => {
                  const tabKey = campaignTabKey(src);
                  return (
                    <button
                      key={src.project_id != null ? String(src.project_id) : tabKey}
                      type="button"
                      role="tab"
                      aria-selected={selectedProjectName === tabKey}
                      className={`campaign-tab-btn${selectedProjectName === tabKey ? ' campaign-tab-btn--active' : ''}`}
                      onClick={() => setSelectedProjectName(tabKey)}
                    >
                      {campaignTabLabel(src)}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Summary Stats */}
            <div className="dv-stats-row">
              <div className="dv-stat">
                <span className="dv-stat-value">{summaryStats.total}</span>
                <span className="dv-stat-label">Total Holes</span>
              </div>
              <div className="dv-stat dv-stat-green">
                <span className="dv-stat-value">{summaryStats.complete}</span>
                <span className="dv-stat-label">Complete</span>
              </div>
              <div className="dv-stat dv-stat-amber">
                <span className="dv-stat-value">{summaryStats.incomplete}</span>
                <span className="dv-stat-label">Incomplete</span>
              </div>
              <div className="dv-stat dv-stat-red">
                <span className="dv-stat-value">{summaryStats.missing}</span>
                <span className="dv-stat-label">Missing Data</span>
              </div>
              <div className="dv-stat dv-stat-red">
                <span className="dv-stat-value">{summaryStats.noActualCoords}</span>
                <span className="dv-stat-label">No Actual/Final Coords</span>
              </div>
            </div>

            {/* Search + Check Filter */}
            <div className="dv-search-bar">
              <input
                type="text"
                placeholder="Filter by hole ID, geologist, or status..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="dv-search-input"
              />
              {searchTerm && (
                <button className="dv-search-clear" onClick={() => setSearchTerm('')}>&times;</button>
              )}
              <select
                value={checkFilter}
                onChange={(e) => setCheckFilter(e.target.value)}
                className="dv-check-filter"
              >
                {CHECK_FILTER_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              {checkFilter && (
                <button className="dv-filter-clear" onClick={() => setCheckFilter('')}>&times;</button>
              )}
              <Button variant="ghost" size="sm" onClick={handleExportCSV} disabled={!filteredData.length}>
                Export CSV
              </Button>
              <span className="dv-search-count">
                {filteredData.length} of {campaignBaseData.length} hole
                {campaignBaseData.length !== 1 ? 's' : ''}
                {checkFilter ? ' failing' : ''}
              </span>
            </div>

            {/* Table */}
            <Card className="dv-table-card">
              <Table
                data={filteredData}
                columns={tableColumns}
                onRowClick={handleRowClick}
                compact
                hoverable
                emptyMessage="No drillholes match your search."
              />
            </Card>
          </>
        )}

        {/* Detail Modal */}
        {selectedHole && (
          <div className="modal-overlay" onClick={handleCloseDetail}>
            <div className="modal-content dv-detail-modal" onClick={(e) => e.stopPropagation()}>
              <Card className="hole-detail-card">
                <div className="modal-header">
                  <h2>Drillhole: {selectedHole.hole_id}</h2>
                  <button className="close-button" onClick={handleCloseDetail}>�</button>
                </div>

                <DetailContent hole={selectedHole} />

                <div className="modal-footer">
                  <Button onClick={handleCloseDetail}>Close</Button>
                </div>
              </Card>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

const DetailContent = ({ hole }) => {
  const collarDepth = toNum(hole.collar_actual_depth) || 0;
  const missingHeaders = parsePostgresArray(hole.header_empty_columns);
  const coordTypes = getCoordinateTypeLabels(hole);
  const surveyDepth = toNum(hole.survey_max_depth);
  const surveyGap = (collarDepth && surveyDepth != null) ? collarDepth - surveyDepth : null;

  return (
    <>
      {/* Collar Information */}
      <div className="detail-section">
        <h3>Collar Information</h3>
        <div className="detail-grid">
          <div className="detail-item">
            <span className="detail-label">Status</span>
            <span className="detail-value">
              <span className={`status-badge ${getStatusBadgeClass(hole.collar_status)}`}>
                {hole.collar_status || 'Unknown'}
              </span>
            </span>
          </div>
          <div className="detail-item">
            <span className="detail-label">Total Depth</span>
            <span className="detail-value">{collarDepth ? `${collarDepth.toFixed(1)}m` : '�'}</span>
          </div>
          <div className="detail-item">
            <span className="detail-label">Geologist</span>
            <span className="detail-value">{hole.geologist || '�'}</span>
          </div>
          <div className="detail-item">
            <span className="detail-label">Geotech</span>
            <span className="detail-value">{hole.geotech || '�'}</span>
          </div>
          {hole.source_project_name && (
            <div className="detail-item">
              <span className="detail-label">Project</span>
              <span className="detail-value">{hole.source_project_name}</span>
            </div>
          )}
          <div className="detail-item">
            <span className="detail-label">Completion</span>
            <span className="detail-value">{computeCompletion(hole)}% ({TOTAL_CHECKS} checks)</span>
          </div>
        </div>
      </div>

      {/* Depth Coverage Visualization */}
      <div className="detail-section">
        <h3>Depth Coverage</h3>
        {collarDepth > 0 ? (
          <DepthChart hole={hole} collarDepth={collarDepth} />
        ) : (
          <p style={{ color: '#6b7280', fontStyle: 'italic' }}>No collar depth recorded.</p>
        )}
      </div>

      {/* Data Checks */}
      <div className="detail-section">
        <h3>
          Data Checks
          <span className="dv-count-badge dv-count-blue">
            {(() => {
              let p = 0;
              for (const cat of LOGGING_CATEGORIES) { if (isCategoryComplete(hole, cat)) p++; }
              if (hasActualCoordinates(hole)) p++;
              return `${p}/${TOTAL_CHECKS}`;
            })()}
          </span>
        </h3>
        <div className="dv-checks-grid">
          <CheckIndicator
            label="Has Actual/Final Coordinates"
            value={hasActualCoordinates(hole)}
          />
          <CheckIndicator
            label="Has Trace Geometry"
            value={hole.has_trace_geom === true || hole.has_trace_geom === 'true'}
          />
          <CheckIndicator
            label={`Survey Within 30m${surveyGap != null ? ` (gap: ${surveyGap.toFixed(1)}m)` : ''}`}
            value={collarDepth > 0 && surveyDepth != null && surveyGap <= 30}
          />
          <CheckIndicator
            label="Lithology Reaches Depth"
            value={isCategoryComplete(hole, LOGGING_CATEGORIES.find(c => c.key === 'lithology'))}
          />
          <CheckIndicator
            label="Oxidation Reaches Depth"
            value={isCategoryComplete(hole, LOGGING_CATEGORIES.find(c => c.key === 'oxidation'))}
          />
          <CheckIndicator
            label="Mag Susc Reaches Depth"
            value={isCategoryComplete(hole, LOGGING_CATEGORIES.find(c => c.key === 'mag_susc'))}
          />
          <CheckIndicator
            label="RQD Reaches Depth"
            value={isCategoryComplete(hole, LOGGING_CATEGORIES.find(c => c.key === 'rqd'))}
          />
          <CheckIndicator
            label="Core Storage Reaches Depth"
            value={isCategoryComplete(hole, LOGGING_CATEGORIES.find(c => c.key === 'corestorage'))}
          />
          <CheckIndicator
            label={
              isSteepnessExempt(hole)
                ? `Core Orientation (steep exempt, min steepness ${toNum(hole.survey_min_steepness)})`
                : 'Core Orientation Reaches Depth'
            }
            value={isCategoryComplete(hole, LOGGING_CATEGORIES.find(c => c.key === 'core_orientation'))}
          />
          <CheckIndicator
            label="Has Planned Coordinates"
            value={coordTypes.some(t => t.toLowerCase().includes('planned'))}
            informational
          />
        </div>
      </div>

      {/* Coordinates */}
      {(coordTypes.length > 0 || hole.coord_row_count != null || hole.trace_row_count != null) && (
        <div className="detail-section">
          <h3>Coordinates</h3>
          <div className="detail-grid">
            {coordTypes.length > 0 && (
              <div className="detail-item">
                <span className="detail-label">Coordinate Types</span>
                <span className="detail-value">
                  <span className="dv-tag-row">
                    {coordTypes.map((t, i) => {
                      const lower = t.toLowerCase();
                      let tagClass = 'dv-tag-default';
                      if (lower.includes('final')) tagClass = 'dv-tag-green';
                      else if (lower.includes('actual')) tagClass = 'dv-tag-orange';
                      return (
                        <span key={i} className={`dv-tag ${tagClass}`}>{t}</span>
                      );
                    })}
                  </span>
                </span>
              </div>
            )}
            {hole.coord_row_count != null && (
              <div className="detail-item">
                <span className="detail-label">Coord Rows</span>
                <span className="detail-value">{hole.coord_row_count}</span>
              </div>
            )}
            {hole.trace_row_count != null && (
              <div className="detail-item">
                <span className="detail-label">Trace Rows</span>
                <span className="detail-value">{hole.trace_row_count}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Missing Header Columns */}
      {missingHeaders.length > 0 && (
        <div className="detail-section">
          <h3>
            Missing Header Fields
            <span className="dv-count-badge dv-count-red">{missingHeaders.length}</span>
          </h3>
          <div className="dv-tag-row">
            {missingHeaders.map((col, i) => (
              <span key={i} className="dv-tag dv-tag-red">{humanize(col)}</span>
            ))}
          </div>
        </div>
      )}
    </>
  );
};

const DepthChart = ({ hole, collarDepth }) => {
  return (
    <div className="dc-wrapper">
      {/* Depth scale on left */}
      <div className="dc-scale">
        <span className="dc-scale-label">0m</span>
        <span className="dc-scale-label dc-scale-mid">{Math.round(collarDepth / 2)}m</span>
        <span className="dc-scale-label">{Math.round(collarDepth)}m</span>
      </div>

      {/* Bars */}
      <div className="dc-bars">
        {LOGGING_CATEGORIES.map(cat => {
          const maxDepth = toNum(hole[cat.depthKey]);
          const rowCount = toNum(hole[cat.countKey]);
          const pct = maxDepth && collarDepth ? Math.min((maxDepth / collarDepth) * 100, 100) : 0;
          const color = getCategoryBarColor(hole, cat);
          const hasData = maxDepth != null && maxDepth > 0;
          const complete = isCategoryComplete(hole, cat);

          return (
            <div key={cat.key} className="dc-bar-col">
              <div className={`dc-bar-container ${complete ? 'dc-bar-complete' : ''}`}>
                <div
                  className="dc-bar-fill"
                  style={{ height: `${pct}%`, backgroundColor: color }}
                  title={`${cat.label}: ${hasData ? `${maxDepth}m (${rowCount || 0} rows)` : 'No data'} � ${complete ? 'Complete' : 'Incomplete'}`}
                >
                  {hasData && pct < 100 && (
                    <span className="dc-bar-depth">{Math.round(maxDepth)}</span>
                  )}
                </div>
                {!hasData && (
                  <div className="dc-bar-empty" title={`${cat.label}: No data`}>
                    <span className="dc-bar-x">�</span>
                  </div>
                )}
              </div>
              <span className="dc-bar-label">{cat.abbr}</span>
              <span className="dc-bar-count">{rowCount != null ? rowCount : '�'}</span>
            </div>
          );
        })}
      </div>

      {/* Reference line at collar depth */}
      <div className="dc-ref-line" title={`Collar Depth: ${collarDepth}m`} />
    </div>
  );
};

const CheckIndicator = ({ label, value, informational }) => {
  const isTrue = value === true;
  const isFalse = value === false;

  let cls = 'check-unknown';
  let icon = '?';
  if (isTrue) { cls = 'check-pass'; icon = '\u2713'; }
  else if (isFalse) { cls = informational ? 'check-info' : 'check-fail'; icon = informational ? 'i' : '\u2717'; }

  return (
    <div className={`dv-check ${cls}`}>
      <span className="dv-check-icon">{icon}</span>
      <span className="dv-check-label">{label}</span>
    </div>
  );
};

export default DrillholeValidation;
